import { Component, OnInit, ViewChild, ElementRef, Input, Output, EventEmitter, AfterViewInit, HostListener, OnChanges, SimpleChanges, OnDestroy, ViewChildren, QueryList } from '@angular/core';
import { MatToolbar } from '@angular/material/toolbar';
import { MatIcon } from '@angular/material/icon';
import { MatCard, MatCardContent, MatCardHeader } from '@angular/material/card';
import { NgClass, NgForOf, NgIf, NgSwitch, NgSwitchCase, TitleCasePipe } from '@angular/common';
import { MatDivider } from '@angular/material/divider';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { DataService, Card } from '../../../services/data.service';
import { finalize } from 'rxjs/operators';
import { FontSizeService, FontSizeOption } from '../../../services/font-size.service';
import { NavigationPathService, NavigationPathItem } from '../../../services/navigation-path.service';
import { ChangeDetectorRef } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LoginGmailComponent } from '../../login-gmail/login-gmail.component';
import { RoleService } from '../../../services/role.service';
import { AuthService } from '../../../services/auth.service';
import { FileProcessingComponent, FileToView } from '../../file-processing/file-processing.component';
import { FileOperationEventsService, FileOperationEvent } from '../../../services/file-operation-events.service';
import { LoadingService } from '../../../services/loading.service';
import { MatDialog } from '@angular/material/dialog';
import { BundleUploadComponent } from '../../bundle-upload/bundle-upload.component';

@Component({
  selector: 'app-main-area',
  standalone: true,
  imports: [
    MatToolbar,
    MatIcon,
    NgIf,
    NgForOf,
    MatIconButton,
    FormsModule,
    ScrollingModule,
    MatProgressSpinnerModule,
    LoginGmailComponent,
    FileProcessingComponent,
    MatButton,
    MatCheckbox,
    MatSelect,
    MatOption
  ],
  templateUrl: './main-area.component.html',
  styleUrl: './main-area.component.css'
})
export class MainAreaComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  // Inputs from parent component
  @Input() p1Mode: 'topicwise' | 'yearwise' = 'topicwise';
  @Input() selectedSubject: string | null = null;
  @Input() selectedSubItem: string | null = null;
  @Input() selectedTopic: string | null = null;
  @Input() selectedSubTopic: string | null = null;
  @Input() selectedYearwiseExam: string | null = null;
  @Input() selectedYearwiseLower: string | null = null;
  @Input() selectedYear: string | null = null;
  @Input() loadedQuestions: Card[] = [];
  @Input() isLoadingQuestions = false;
  @Input() isMobileView = false;
  @Input() navigationPathItems: NavigationPathItem[] = [];
  @Input() isAdminMode: boolean = false;
  @Input() hindiNameMap: { [key: string]: string } = {};
  @Input() isInternalUser: boolean = false;

  // Navigation path expansion state for mobile view
  isNavPathExpanded = false;

  // Loading and error states
  hasQuestionsError = false;
  questionsErrorMessage = '';

  // Cards data
  allCards: Card[] = [];
  displayedCards: Card[] = [];
  selectedCard: Card | null = null;

  // Display state
  allExpanded = true;

  // Display mode options
  displayModes = [
    { value: 'allAtOnce', label: 'Load All Questions (At Once)' },
    { value: 'lazyLoad', label: 'Load All Questions (Lazy)' },
    { value: 'paginated10', label: 'Paginated (10 per page)' },
    { value: 'single', label: 'Single Question View' }
  ];
  displayMode: string = 'allAtOnce'; // Default

  // Language selection
  languageOptions = [
    { value: 'bilingual', label: 'Bilingual' },
    { value: 'englishOnly', label: 'English Only' },
    { value: 'hindiOnly', label: 'Hindi Only' }
  ];
  selectedLanguage: string = 'bilingual'; // Default

  // Content type selection
  contentTypeOptions = [
    { value: 'imageAndText', label: 'Image+Text view' },
    { value: 'imageOnly', label: 'Image view' },
    { value: 'textOnly', label: 'Text view' }
  ];
  selectedContentType: string = 'imageAndText'; // Default

  // Pagination
  pageSize = 10;
  currentPage = 1;
  totalPages = 1;

  // Lazy loading
  lazyBatchSize = 3;
  lazyLoadedCount = 0;
  isLoadingMoreLazy = false;

  // Single view
  currentSingleIndex = 0;

  // Page input validation
  invalidPageInput = false;

  // Show details toggle
  showDetails = false; // Default to hiding details pane

  // Outputs to parent component
  @Output() selectedCardChange = new EventEmitter<Card | null>();
  @Output() showDetailsChange = new EventEmitter<boolean>();
  @Output() viewModeChange = new EventEmitter<string>();
  @Output() toggleMobileNav = new EventEmitter<void>();

  // Update property to remove 'main' option
  viewMode: 'split' | 'fullscreen' = 'split';

  @ViewChild('paneContent') paneContent!: ElementRef;

  // Add these properties at the class level, near other component properties
  private lastApiCallParams?: { mode: string, param: string };
  private apiDebounceTimer?: any;
  private readonly API_DEBOUNCE_TIME = 300; // ms

  // Add a Subject for subscription management
  private destroy$ = new Subject<void>();

  // Add currentFontSize property
  currentFontSize: FontSizeOption = 'medium';

  // Add new user related properties
  isUserLoggedIn = false;
  showLoginPrompt = false;
  showDownloadMessage = false;

  // File viewer variables
  fileToView: FileToView | null = null;
  fileLoading = false; // Tracks when a file is being loaded

  // Height and Preview selector properties
  selectedHeight: string = 'large'; // Default to large height
  selectedPreview: string = 'preview-all'; // Default to show all previews

  @ViewChildren(FileProcessingComponent) fileProcessingComponents!: QueryList<FileProcessingComponent>;

  constructor(
    private dataService: DataService,
    private fontSizeService: FontSizeService,
    private navigationPathService: NavigationPathService,
    private cdr: ChangeDetectorRef,
    private roleService: RoleService,
    private authService: AuthService,
    private fileOpEvents: FileOperationEventsService,
    private loadingService: LoadingService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    console.log('MainAreaComponent: ngOnInit() called');

    // Initialize height selector to large (200px) on component init
    this.onHeightChange();

    // Check if user is logged in
    this.checkUserLoginStatus();

    // Get language preference from localStorage (default to bilingual if not set)
    const savedLanguage = localStorage.getItem('languagePreference');
    if (savedLanguage) {
      this.selectedLanguage = savedLanguage;
      console.log('Retrieved language preference from localStorage:', this.selectedLanguage);
    }

    // Subscribe to font size changes
    this.fontSizeService.fontSize$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(fontSize => {
      this.currentFontSize = fontSize;
      this.cdr.detectChanges();
    });

    // Subscribe to navigation path changes
    this.navigationPathService.navigationPath$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(pathItems => {
      this.navigationPathItems = pathItems;
      this.cdr.detectChanges();
    });

    // Get the current navigation path immediately without waiting for first emission
    this.navigationPathService.getCurrentPath().subscribe((path: NavigationPathItem[]) => {
      if (path && path.length > 0) {
        this.navigationPathItems = path;
        this.cdr.detectChanges();
      }
    });

    // Subscribe to auth service for login status changes
    this.authService.authState$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      // Re-check user status when auth state changes
      this.checkUserLoginStatus();
    });

    this.fileOpEvents.fileOperation$.subscribe((event: FileOperationEvent) => {
      if (event === 'refresh' || event === 'deleted') {
        this.loadFileBundle(); // Call the loadFileBundle method to refresh files
      }
    });

    // Signal that the main area has finished its initial loading
    setTimeout(() => {
      this.loadingService.setMainAreaLoaded();
    }, 800);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Check if selectedSubject or selectedSubItem has changed
    if ((changes['selectedSubject'] && !changes['selectedSubject'].firstChange) ||
        (changes['selectedSubItem'] && !changes['selectedSubItem'].firstChange)) {

      // Only trigger bundle reload if we have both subject and section
      if (this.selectedSubject && this.selectedSubItem) {
        setTimeout(() => this.loadFileBundle(), 0);
      }
    }

    // Check if loadedQuestions changed and update if needed
    if (changes['loadedQuestions']) {
      this.updateCards();

      // When questions are loaded, select the first question by default in desktop mode
      if (this.allCards && this.allCards.length > 0 && !this.isMobileView) {
        this.selectedCard = this.allCards[0];
        // Emit the selected card to show the details area
        this.selectedCardChange.emit(this.selectedCard);

        // Ensure details area is shown
        this.showDetails = true;
        this.showDetailsChange.emit(true);
      }
    }

    // Check if isLoadingQuestions changed
    if (changes['isLoadingQuestions']) {
      if (!this.isLoadingQuestions && this.displayMode) {
        // If loading is complete and we have a display mode, re-apply it
        this.applyDisplayMode();
      }
    }

    // Handle mobile view changes
    if (changes['isMobileView']) {
      // If switching to mobile view, we need to ensure the internal state is correct
      if (this.isMobileView) {
        // In mobile view:
        // 1. Don't show the "Show Details" checkbox
        // 2. Still maintain the showDetails value for when returning to desktop
        // 3. Emit the change so parent component knows details should be shown separately in mobile

      } else {
        // When returning to desktop view, emit the stored value to restore previous state
        this.showDetailsChange.emit(this.showDetails);
      }
    }

    // Clear any pending API calls when new selection changes are made
    if (this.apiDebounceTimer) {
      clearTimeout(this.apiDebounceTimer);
      this.apiDebounceTimer = undefined;
    }

    // Handle directly loaded questions from parent component
    if (changes['loadedQuestions']) {
      // Check parent's loading state - don't update if loading to prevent flickering
      if (this.isLoadingQuestions) {
        return; // Skip processing while loading
      }

      // Set allCards to loadedQuestions (even if empty)
      this.allCards = this.loadedQuestions || [];

      // Apply the display mode (this will also handle empty arrays properly)
      this.applyDisplayMode();

      // Only show error if we truly have no questions AND we're not in a loading state
      if (Array.isArray(this.loadedQuestions) && this.loadedQuestions.length === 0 && !this.isLoadingQuestions) {
        this.hasQuestionsError = true;
        this.questionsErrorMessage = `No questions available for the selected topic.`;
      } else if (this.loadedQuestions && this.loadedQuestions.length > 0) {
        // Make sure error state is cleared if we have questions
        this.hasQuestionsError = false;
        this.questionsErrorMessage = '';
      }

      return; // Skip API calls as we've already updated the UI
    }

    this.apiDebounceTimer = setTimeout(() => {
      // For topicwise mode, ONLY call loadQuestions when subtopic is selected
      if (this.p1Mode === 'topicwise') {
        // Check if selectedSubTopic has changed and has a valid value
        if (changes['selectedSubTopic'] && this.selectedSubTopic) {
          // Skip empty subtopics
          if (!this.selectedSubTopic.trim()) {
            return;
          }

          // Skip duplicate calls
          const apiParams = { mode: 'topicwise', param: this.selectedSubTopic };
          if (this.lastApiCallParams &&
            this.lastApiCallParams.mode === apiParams.mode &&
            this.lastApiCallParams.param === apiParams.param) {
            return;
          }

          this.lastApiCallParams = apiParams;
          this.loadQuestions(this.selectedSubTopic);
        } else {
          // Do not load questions for subject/section/topic changes
        }
      }

      // For yearwise mode, ONLY call loadQuestions when year is selected
      else if (this.p1Mode === 'yearwise') {
        // Only load questions if ALL required yearwise selections are made AND year specifically changed
        if (this.selectedYearwiseExam &&
          this.selectedYearwiseLower &&
          this.selectedYear &&
          (changes['selectedYear'] || changes['selectedYearwiseLower'] || changes['selectedYearwiseExam'])) {

          const yearwiseQuery = `${this.selectedYearwiseExam}-${this.selectedYearwiseLower}-${this.selectedYear}`;

          // Check if we're trying to load the same parameters again
          const apiParams = { mode: 'yearwise', param: yearwiseQuery };

          if (this.lastApiCallParams &&
            this.lastApiCallParams.mode === apiParams.mode &&
            this.lastApiCallParams.param === apiParams.param) {
            return;
          }

          this.lastApiCallParams = apiParams;
          this.loadQuestions(yearwiseQuery);
        } else {
          // Do not load questions for exam/lower selections
        }
      }
    }, this.API_DEBOUNCE_TIME);
  }

  ngAfterViewInit() {
    // Apply custom class to select panel
    setTimeout(() => {
      const panelClass = document.querySelector('.mat-mdc-select-panel');
      if (panelClass) {
        panelClass.classList.add('modern-select-panel');
      }
    }, 0);
  }

  // Load questions for a subtopic
  loadQuestions(subtopic: string): void {
    if (!subtopic) {
      return;
    }

    // Prevent duplicate API calls for the same parameters
    const apiParams = {mode: this.p1Mode, param: subtopic};
    if (this.lastApiCallParams &&
      this.lastApiCallParams.mode === apiParams.mode &&
      this.lastApiCallParams.param === apiParams.param) {
      return;
    }

    // Clear previous timer if exists
    if (this.apiDebounceTimer) {
      clearTimeout(this.apiDebounceTimer);
    }

    // Add debounce to avoid rapid successive API calls
    this.apiDebounceTimer = setTimeout(() => {
      this.lastApiCallParams = apiParams;
      this._loadQuestionsImpl(subtopic);
    }, this.API_DEBOUNCE_TIME);
  }

  private _loadQuestionsImpl(subtopic: string): void {
    this.dataService.getQuestions(subtopic)
      .pipe(
        finalize(() => {
          // We don't clear loading state here as it's controlled by parent
        })
      )
      .subscribe({
        next: (questions) => {
          // Ensure questions is treated as an array
          if (!Array.isArray(questions)) {
            this.allCards = [];
            this.hasQuestionsError = true;
            this.questionsErrorMessage = `Received invalid data for '${subtopic}'.`;
          } else {
            this.allCards = questions;

            // Set error state only if array is empty
            if (questions.length === 0) {
              this.hasQuestionsError = true;
              this.questionsErrorMessage = `No questions available for '${subtopic}'.`;
            } else {
              // Clear error state if we have questions
              this.hasQuestionsError = false;
              this.questionsErrorMessage = '';
            }
          }

          // Always apply display mode to update UI
          this.applyDisplayMode();
        },
        error: (error) => {
          // Handle API errors
          this.hasQuestionsError = true;
          this.questionsErrorMessage = `Failed to load questions.`;

          // Set empty cards array when API fails
          this.allCards = [];
          this.applyDisplayMode();
        }
      });
  }

  // ─────────────────────────────────────────────────────────
  // Handling the drop-down choice
  // ─────────────────────────────────────────────────────────
  onDisplayModeChange(): void {
    // Reset any state needed and re-apply the selected mode
    this.currentPage = 1;
    this.invalidPageInput = false; // Reset invalid page input state
    this.applyDisplayMode();
  }

  applyDisplayMode(): void {
    this.selectedCard = null;   // Reset selected
    // Emit null selected card to the parent to clear details area
    this.selectedCardChange.emit(null);

    // Reset expansions
    this.allExpanded = true;
    // Reset lazy loading state
    this.isLoadingMoreLazy = false;

    // Make sure allCards is populated
    if (!this.allCards || this.allCards.length === 0) {
      this.displayedCards = [];
      // Make sure we clear the selected card when there are no questions
      this.selectedCard = null;
      this.selectedCardChange.emit(null);
      return; // No cards to display
    }

    console.log(`Applying display mode: ${this.displayMode} for ${this.allCards.length} cards`);

    // Set the first card as selected by default only in desktop mode and emit it
    if (this.allCards.length > 0 && !this.isMobileView) {
      this.selectedCard = this.allCards[0];
      // Emit the first card by default only in desktop mode
      this.selectedCardChange.emit(this.selectedCard);

      // Always show details when selecting first card in desktop mode
      this.showDetails = true;
      this.showDetailsChange.emit(true);
    }

    switch (this.displayMode) {
      case 'allAtOnce':
        // Load everything at once - make a complete copy to ensure all items are displayed
        this.displayedCards = [...this.allCards];
        console.log(`allAtOnce mode - displaying all ${this.displayedCards.length} cards`);
        break;

      case 'lazyLoad':
        // Start with more items initially for better UX
        this.lazyBatchSize = 20; // Increase batch size for better initial experience
        this.lazyLoadedCount = Math.min(this.lazyBatchSize, this.allCards.length);
        this.displayedCards = this.allCards.slice(0, this.lazyLoadedCount);
        console.log(`Initial lazy load: ${this.lazyLoadedCount} of ${this.allCards.length} questions`);

        // Force a check for more content after a short delay
        // This helps if the initial content doesn't fill the viewport
        setTimeout(() => {
          this.checkForMoreContent();
        }, 1000);
        break;

      case 'paginated10':
        this.pageSize = 10; // Show 10 items per page to match name
        this.currentPage = 1;
        this.setupPagination();
        console.log(`Paginated mode - page size: ${this.pageSize}, total items: ${this.allCards.length}, total pages: ${this.totalPages}`);
        break;

      case 'single':
        // Show first question
        this.currentSingleIndex = 0;
        if (this.allCards.length > 0) {
          this.displayedCards = [this.allCards[this.currentSingleIndex]];
          // Log the question text for debugging
          console.log(`Single view mode - showing 1 of ${this.allCards.length} questions`);

          // Allow time for the DOM to update, then adjust layout
          setTimeout(() => {
            this.adjustSingleViewLayout();
          }, 100);
        } else {
          this.displayedCards = [];
        }
        break;
    }

    // Apply language mode
    this.applyLanguageMode();
    // Apply content type
    this.applyContentTypeMode();
  }

  // New method to adjust the layout in single view mode
  adjustSingleViewLayout(): void {
    if (this.displayMode !== 'single') return;

    // Log that we're adjusting the layout
    console.log('Adjusting single view layout');

    // Get references to the elements
    if (this.paneContent && this.paneContent.nativeElement) {
      setTimeout(() => {
        const singleView = this.paneContent.nativeElement.querySelector('.single-question-view');
        if (singleView) {
          const textPortion = singleView.querySelector('.text-portion');
          const imagePortion = singleView.querySelector('.image-portion');
          const paneFooter = this.paneContent.nativeElement.querySelector('.pane-footer');

          if (textPortion && imagePortion && paneFooter) {
            // Get the available height
            const totalHeight = singleView.clientHeight;
            const textHeight = textPortion.scrollHeight;
            const footerHeight = paneFooter.clientHeight;
            const navigationHeight = singleView.previousElementSibling ? singleView.previousElementSibling.clientHeight : 0;

            // Calculate the remaining height for the image, accounting for margins and padding
            const availableHeight = totalHeight - textHeight - 32; // 32px for margins/padding
            const imageHeight = Math.max(200, availableHeight);

            // Log the measurements for debugging
            console.log(`Single view layout: Total height=${totalHeight}px, Text height=${textHeight}px, Footer height=${footerHeight}px, Nav height=${navigationHeight}px, Available height=${availableHeight}px, Image height=${imageHeight}px`);

            // Apply the calculated height to the image portion
            imagePortion.style.height = `${imageHeight}px`;

            // Ensure the image fills the available space
            const img = imagePortion.querySelector('img');
            if (img) {
              // Force the image to redraw at the new size
              img.style.objectFit = 'contain';
            }
          }
        }
      }, 100); // Small delay to ensure DOM is updated
    }
  }

  // ─────────────────────────────────────────────────────────
  // Lazy Loading: detect scroll at bottom
  // ─────────────────────────────────────────────────────────
  @HostListener('scroll', ['$event'])
  onPaneScroll(e: Event) {
    // If not in lazyLoad mode, do nothing
    if (this.displayMode !== 'lazyLoad') return;

    this.checkForMoreContent();
  }

  // Separate method to check if we need to load more content
  // This can be called from scroll event or programmatically
  checkForMoreContent(): void {
    // If not in lazyLoad mode or already loading, do nothing
    if (this.displayMode !== 'lazyLoad' || this.isLoadingMoreLazy) return;

    // Use the paneContent reference directly
    if (this.paneContent && this.paneContent.nativeElement) {
      const element = this.paneContent.nativeElement;
      const scrollableContent = element.querySelector('.pane-content');

      if (scrollableContent) {
        const threshold = scrollableContent.scrollHeight - scrollableContent.scrollTop - scrollableContent.clientHeight;

        // If near the bottom (within 500px), load next batch
        if (threshold < 500 && !this.isLoadingMoreLazy) {
          this.loadMoreLazy();
        }
      } else {
        console.warn('Could not find scrollable content element');
      }
    }
  }

  loadMoreLazy(): void {
    // Add next batch if available
    if (this.lazyLoadedCount < this.allCards.length && !this.isLoadingMoreLazy) {
      this.isLoadingMoreLazy = true;

      // Simulate network delay for smoother UX
      setTimeout(() => {
        // Calculate how many more items to load - load more at once for better UX
        const remainingItems = this.allCards.length - this.lazyLoadedCount;
        const itemsToLoad = Math.min(20, remainingItems); // Load 20 items at a time for better performance

        this.lazyLoadedCount += itemsToLoad;
        this.displayedCards = this.allCards.slice(0, this.lazyLoadedCount);

        // Keep the loading indicator visible a bit longer for better UX
        setTimeout(() => {
          this.isLoadingMoreLazy = false;

          // Check if we need to load more after a short delay
          // This helps if the loaded content doesn't fill the viewport
          setTimeout(() => {
            this.checkForMoreContent();
          }, 300);
        }, 300);
      }, 500); // Shorter delay for better responsiveness
    }
  }

  // ─────────────────────────────────────────────────────────
  // Pagination (10/page) or single
  // ─────────────────────────────────────────────────────────
  setupPagination(): void {
    // For 10/page mode
    if (this.displayMode === 'paginated10') {
      this.totalPages = Math.ceil(this.allCards.length / this.pageSize);
      this.showCurrentPage();
    }
  }

  showCurrentPage(): void {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.displayedCards = this.allCards.slice(startIndex, endIndex);

    // We only set the selectedCard in desktop mode when initially loading
    // but don't emit unless specifically clicked by the user
    if (this.displayedCards.length > 0 && !this.isMobileView && this.selectedCard === null) {
      this.selectedCard = this.displayedCards[0];
      // We don't emit here to avoid showing details automatically when changing pages
    }
  }

  goToPreviousPage(): void {
    if (this.displayMode === 'paginated10') {
      if (this.currentPage > 1) {
        this.currentPage--;
        const startIndex = (this.currentPage - 1) * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        this.displayedCards = this.allCards.slice(startIndex, endIndex);

        // Don't automatically select any card when changing pages
        // Let user click to select which question to view in details area
      }
    } else if (this.displayMode === 'single') {
      if (this.currentSingleIndex > 0) {
        this.currentSingleIndex--;
        this.displayedCards = [ this.allCards[this.currentSingleIndex] ];

        // Don't automatically emit the card selection - let user click to view details
        // User must explicitly click on the question to see details

        // Adjust layout after changing the question
        setTimeout(() => {
          this.adjustSingleViewLayout();
        }, 100);
      }
    }
  }

  goToNextPage(): void {
    if (this.displayMode === 'paginated10') {
      const totalPages = Math.ceil(this.allCards.length / this.pageSize);
      if (this.currentPage < totalPages) {
        this.currentPage++;
        const startIndex = (this.currentPage - 1) * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        this.displayedCards = this.allCards.slice(startIndex, endIndex);

        // Don't automatically select any card when changing pages
        // Let user click to select which question to view in details area
      }
    } else if (this.displayMode === 'single') {
      if (this.currentSingleIndex < this.allCards.length - 1) {
        this.currentSingleIndex++;
        this.displayedCards = [ this.allCards[this.currentSingleIndex] ];

        // Don't automatically emit the card selection - let user click to view details
        // User must explicitly click on the question to see details

        // Adjust layout after changing the question
        setTimeout(() => {
          this.adjustSingleViewLayout();
        }, 100);
      }
    }
  }

  goToPage(page: number): void {
    // Reset invalid state at the beginning
    this.invalidPageInput = false;

    // Ensure page is a valid number
    if (isNaN(page)) {
      this.invalidPageInput = true;
      return;
    }

    // If user types in the page number box
    if (this.displayMode === 'paginated10') {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        const startIndex = (this.currentPage - 1) * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        this.displayedCards = this.allCards.slice(startIndex, endIndex);

        // Update the selected card for internal tracking but don't emit
        if (this.displayedCards.length > 0) {
          this.selectedCard = this.displayedCards[0];
          // We don't emit here to avoid showing details: this.selectedCardChange.emit(this.selectedCard);
        }
      } else {
        this.invalidPageInput = true;
        console.warn(`Invalid page number: ${page}. Valid range: 1-${this.totalPages}`);
      }
    } else if (this.displayMode === 'single') {
      // For single view, page number is the question index (1-indexed)
      if (page >= 1 && page <= this.allCards.length) {
        this.currentSingleIndex = page - 1; // Convert to 0-indexed
        this.displayedCards = [this.allCards[this.currentSingleIndex]];
        // Update the selected card for internal tracking but don't emit
        this.selectedCard = this.allCards[this.currentSingleIndex];
        // We don't emit here to avoid showing details: this.selectedCardChange.emit(this.selectedCard);
        console.log(`Navigated to question ${page} in single view mode`);

        // Adjust layout after changing the question
        setTimeout(() => {
          this.adjustSingleViewLayout();
        }, 100);
      } else {
        this.invalidPageInput = true;
        console.warn(`Invalid question number: ${page}. Valid range: 1-${this.allCards.length}`);
      }
    }
  }

  // ─────────────────────────────────────────────────────────
  // Card expansions + selection for Pane 4
  // ─────────────────────────────────────────────────────────
  toggleAllCards() {
    this.allExpanded = !this.allExpanded;

    // Only apply to cards if not in single view mode
    if (this.displayMode !== 'single') {
      this.displayedCards.forEach(card => card.collapsed = !this.allExpanded);
    }
  }

  toggleCard(card: Card) {
    card.collapsed = !card.collapsed;
  }

  // Select a card (question)
  selectCard(card: Card): void {
    console.log('Card selected:', card);

    // Update selected card
    this.selectedCard = card;

    // Always set showDetails to true when a card is selected
    this.showDetails = true;

    // First emit the showDetails change to ensure the details area is visible
    this.showDetailsChange.emit(true);

    // Then emit the card selection after a small delay to ensure details area is ready
    setTimeout(() => {
      this.selectedCardChange.emit(card);

      // Try to scroll to the details area component
      setTimeout(() => {
        const detailsElement = document.querySelector('app-details-area');
        if (detailsElement) {
          console.log('Scrolling to details area after card selection');
          detailsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }, 50);
  }

  // Open a dialog to jump to a specific page
  openPageDialog(): void {
    const maxPage = this.displayMode === 'single' ? this.allCards.length : this.totalPages;
    const currentPage = this.displayMode === 'single' ? this.currentSingleIndex + 1 : this.currentPage;

    // Use browser's prompt for simplicity
    const input = prompt(`Enter page number (1-${maxPage}):`, currentPage.toString());

    if (input !== null) {
      const page = parseInt(input, 10);
      if (!isNaN(page)) {
        this.goToPage(page);
      }
    }
  }

  // Method to handle language selection change
  onLanguageChange(): void {
    console.log('Language mode changed to:', this.selectedLanguage);
    this.applyLanguageMode();
  }

  // Method to handle content type selection change
  onContentTypeChange(): void {
    console.log('Content type changed to:', this.selectedContentType);
    this.applyContentTypeMode();
  }

  // Apply the selected language mode
  applyLanguageMode(): void {
    // Implementation will depend on the actual data structure
    // For now, we'll just log the change
    console.log('Applying language mode:', this.selectedLanguage);

    // In a real implementation, this would filter or transform the displayed cards
    // based on the selected language
  }

  // Apply the selected content type mode
  applyContentTypeMode(): void {
    // Implementation will depend on the actual data structure
    // For now, we'll just log the change
    console.log('Applying content type mode:', this.selectedContentType);

    // In a real implementation, this would adjust the display to show
    // images, text, or both based on the selected content type
  }

  // Handle show details visibility change
  onShowDetailsChange(): void {
    console.log('Show details changed to:', this.showDetails);
    // Emit to parent component to update the visibility
    this.showDetailsChange.emit(this.showDetails);
    // Force UI update
    this.cdr.detectChanges();
  }

  // Get the appropriate icon for display mode
  getDisplayModeIcon(): string {
    switch (this.displayMode) {
      case 'allAtOnce':
        return 'view_list';
      case 'lazyLoad':
        return 'autorenew';
      case 'paginated10':
        return 'view_module';
      case 'single':
        return 'visibility';
      default:
        return 'dashboard';
    }
  }

  // Get the appropriate icon for language selection
  getLanguageIcon(): string {
    switch (this.selectedLanguage) {
      case 'bilingual':
        return 'language';
      case 'englishOnly':
        return 'abc';
      case 'hindiOnly':
        return 'translate';
      default:
        return 'language';
    }
  }

  // Get the appropriate icon for content type selection
  getContentTypeIcon(): string {
    switch (this.selectedContentType) {
      case 'imageAndText':
        return 'view_carousel';
      case 'imageOnly':
        return 'image';
      case 'textOnly':
        return 'text_fields';
      default:
        return 'view_carousel';
    }
  }

  // Get the appropriate icon for the current view mode
// Set the view mode and emit change event
  setViewMode(mode: 'split' | 'fullscreen'): void {
    if (this.viewMode !== mode) {
      this.viewMode = mode;
      this.viewModeChange.emit(mode);
    }
  }

  // Add method to toggle mobile navigation
  onToggleMobileNav(): void {
    this.toggleMobileNav.emit();
  }

  toggleShowDetails(): void {
    // this.showDetails = !this.showDetails;
    this.onShowDetailsChange();
  }

  // Toggle the navigation path expanded state in mobile view
  toggleNavPathExpanded(): void {
    this.isNavPathExpanded = !this.isNavPathExpanded;
  }

  // Implement ngOnDestroy to clean up subscriptions
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Add updateCards method to process loadedQuestions
  updateCards(): void {
    // Update allCards from loadedQuestions
    this.allCards = this.loadedQuestions ? [...this.loadedQuestions] : [];

    // Apply current display mode to new cards
    if (this.displayMode) {
      this.applyDisplayMode();
    } else {
      // Default to paginated10 if no display mode is set
      this.displayMode = 'paginated10';
      this.applyDisplayMode();
    }

    // Reset pagination
    this.currentPage = 1;
    this.currentSingleIndex = 0;

    console.log(`Updated cards: ${this.allCards.length} questions loaded`);
  }

  // Method to format a node name based on language setting
  formatNodeName(englishName: string, hindiName?: string): string {
    if (!hindiName) return englishName;

    // Get the language preference from localStorage (case insensitive comparison)
    const userLanguagePref = localStorage.getItem('languagePreference') || 'bilingual';

    // Convert to lowercase for consistent comparison
    const languagePref = userLanguagePref.toLowerCase();

    // Check for English only - handle various possible formats
    if (languagePref === 'englishonly' || languagePref === 'english only' || languagePref === 'english_only') {
      return englishName;
    }

    // Check for Hindi only
    if (languagePref === 'hindionly' || languagePref === 'hindi only' || languagePref === 'hindi_only') {
      return hindiName || englishName;
    }

    // Default to bilingual
    return `${englishName} ${hindiName ? `(${hindiName})` : ''}`;
  }

  // Check user login status and role
  checkUserLoginStatus(): void {
    const storedUser = localStorage.getItem('googleUser');
    this.isUserLoggedIn = !!storedUser;

    // Check if user is internal
    const userRole = localStorage.getItem('userRole');
    this.isInternalUser = userRole === 'internal';

    console.log('User login status checked:', {
      isLoggedIn: this.isUserLoggedIn,
      role: userRole,
      isInternal: this.isInternalUser
    });
  }

  // Handle click on download button for not-logged-in users
  onDownloadClick(): void {
    this.showLoginPrompt = true;
    this.showDownloadMessage = false;
  }

  // Handle click on download button for logged-in users
  onLoggedInDownloadClick(): void {
    this.showDownloadMessage = true;
    this.showLoginPrompt = false;

    // Automatically hide the message after 3 seconds
    setTimeout(() => {
      this.showDownloadMessage = false;
      this.cdr.detectChanges();
    }, 3000);
  }

  // Handle user logged in event from login-gmail component
  onUserLoggedIn(userName: string | null): void {
    if (userName) {
      this.isUserLoggedIn = true;
      this.showLoginPrompt = false;

      // Check if user is internal
      const userRole = localStorage.getItem('userRole');
      this.isInternalUser = userRole === 'internal';

      console.log('User logged in:', {
        userName,
        role: userRole,
        isInternal: this.isInternalUser
      });

      this.cdr.detectChanges();
    }
  }

  // Internal user action handlers
  onViewAction(): void {
    console.log('View action clicked');
    // Implement view action for internal users
  }

  onInsertAction(): void {
    console.log('Insert action clicked');
    // Implement insert action for internal users
  }

  onUpdateAction(): void {
    console.log('Update action clicked');
    // Implement update action for internal users
  }

  onDeleteAction(): void {
    console.log('Delete action clicked');
    // Implement delete action for internal users
  }

  // Handle view file request from FileProcessingComponent
  handleViewFile(fileData: FileToView): void {
    console.log('Received file to view:', fileData);
    this.fileToView = fileData;

    // Set loading state based on isLoading flag from the FileToView object
    this.fileLoading = fileData.isLoading || false;

    // Make sure details pane is visible
    if (!this.showDetails) {
      this.showDetails = true;
      this.onShowDetailsChange();
    }

    // If this is just a loading notification, don't create a card yet
    if (this.fileLoading) {
      console.log('File is loading, showing loading state in details pane');
      return;
    }

    // Create a special "file" type card with file content in the questionText or images
    const fileCard: Card = {
      id: Date.now(),
      questionText: `File: ${fileData.fileName}`, // Use standardized format with File: prefix
      // Handle text files specially by storing the content in answerText
      answerText: fileData.fileType === 'txt' && typeof fileData.content === 'string'
        ? fileData.content  // Store actual text content
        : '',  // Don't store content here for other file types
      explanationText: `File type: ${fileData.fileType.toUpperCase()}`,
      // For file previews, use the content URL
      imageUrl: fileData.fileType !== 'txt' ? fileData.content as any : '',
      answerImageUrl: '',
      explanationImageUrl: '',
      // Store the complete file data for the details view
      fileData: fileData
    };

    // Log detailed information for debugging
    console.log('Setting selected card for file view:', {
      fileType: fileData.fileType,
      fileName: fileData.fileName,
      contentType: typeof fileData.content,
      needsExternalViewer: fileData.needsExternalViewer,
      originalUrl: fileData.originalUrl
    });

    // Set selected card to trigger details view
    this.selectedCard = fileCard;
    this.selectedCardChange.emit(fileCard);
  }

  // Handle file operations from details area
  handleFileDownload(): void {
    if (this.selectedCard && this.selectedCard.fileData) {
      const fileType = this.selectedCard.fileData.fileType;
      console.log(`Downloading file of type: ${fileType}`);

      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = this.selectedCard.imageUrl as string;
      link.download = this.selectedCard.fileData.fileName || `file.${fileType}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  handleFileReplace(): void {
    if (this.selectedCard && this.selectedCard.fileData) {
      const fileType = this.selectedCard.fileData.fileType;
      console.log(`Replacing file of type: ${fileType}`);

      // Get the first FileProcessingComponent instance
      const fileProcessingComponent = this.fileProcessingComponents?.first;
      if (fileProcessingComponent) {
        // Call the replaceFile method directly
        fileProcessingComponent.replaceFile(fileType);
      } else {
        console.error('File processing component not found for replace operation');
      }
    }
  }

  handleFileDelete(): void {
    if (this.selectedCard && this.selectedCard.fileData) {
      const fileType = this.selectedCard.fileData.fileType;
      console.log(`Deleting file of type: ${fileType}`);

      // Get the first FileProcessingComponent instance
      const fileProcessingComponent = this.fileProcessingComponents?.first;
      if (fileProcessingComponent) {
        // Call the deleteProcessingFileFromBundle method with bundle index
        fileProcessingComponent.deleteProcessingFileFromBundle(fileType, fileProcessingComponent.selectedBundleIndex);

        // Clear the selected card to go back to default view
        this.selectedCard = null;
        this.selectedCardChange.emit(null);
      } else {
        console.error('File processing component not found for delete operation');
      }
    }
  }

  // Method to load the file bundle by calling the method on FileProcessingComponent
  loadFileBundle(): void {
    console.log('MainAreaComponent: Refreshing file bundle');
    // Get the file processing component and call loadFileBundle on it
    const fileProcessingComponent = this.fileProcessingComponents?.first;
    if (fileProcessingComponent) {
      console.log('Found FileProcessingComponent, calling loadFileBundle()');

      // Remember current file type if there is a selected file
      let currentFileType: string | null = null;
      if (this.selectedCard && this.selectedCard.fileData) {
        currentFileType = this.selectedCard.fileData.fileType;
      }

      // Clear selected card to force refresh
      this.selectedCard = null;
      this.selectedCardChange.emit(null);

      // We can't pass parameters to loadFileBundle, but we can modify the component's
      // input properties before calling the method to achieve the same effect
      if (!this.selectedSubject || !this.selectedSubItem) {
        console.log('No subject or section selected, loading all bundles by default');
        // Temporarily clear the subject and section to force loading all bundles
        const origSubject = fileProcessingComponent.selectedSubject;
        const origSection = fileProcessingComponent.selectedSection;

        fileProcessingComponent.selectedSubject = null;
        fileProcessingComponent.selectedSection = null;
        fileProcessingComponent.loadFileBundle();

        // Restore original values after calling the method
        fileProcessingComponent.selectedSubject = origSubject;
        fileProcessingComponent.selectedSection = origSection;
      } else {
        // Normal case: load bundles for the specific subject and section
        // Make sure the component's input properties are set correctly
        fileProcessingComponent.selectedSubject = this.selectedSubject;
        fileProcessingComponent.selectedSection = this.selectedSubItem;
        fileProcessingComponent.loadFileBundle();
      }

      // If we had a file selected, re-select it after a delay to allow the bundle to load
      if (currentFileType) {
        setTimeout(() => {
          if (fileProcessingComponent && fileProcessingComponent.fileExists(currentFileType!)) {
            console.log(`Re-viewing file of type: ${currentFileType} after refresh`);
            // This will create a fresh card with the updated file info
            fileProcessingComponent.viewFile(currentFileType!);
          }
        }, 800); // Slightly longer timeout to ensure file bundle is fully loaded
      }
    } else {
      console.error('FileProcessingComponent not found for refresh operation');
    }
  }

  // Add this method to update admin mode
  setAdminMode(isAdmin: boolean): void {
    this.isAdminMode = isAdmin;
    console.log('Main area admin mode updated:', this.isAdminMode);

    // Refresh file bundle to apply admin mode changes
    this.loadFileBundle();
  }

  /**
   * Opens the upload bundle dialog
   */
  openUploadBundleDialog(): void {
    // Open the dialog
    this.dialog.open(BundleUploadComponent, {
      width: '1000px',
      maxWidth: '90vw',
      disableClose: false
    });
  }

  // Add method to update height selector
  onHeightChange(): void {
    console.log('Height selector changed to:', this.selectedHeight);

    // Set the CSS variable based on selected height
    let heightValue = '200px'; // Default to large
    switch (this.selectedHeight) {
      case 'small':
        heightValue = '80px';
        break;
      case 'medium':
        heightValue = '150px';
        break;
      case 'large':
        heightValue = '300px';
        break;
    }

    // Update the CSS variable for file cell height at both root and document level
    document.documentElement.style.setProperty('--file-cell-height', heightValue);
    document.body.style.setProperty('--file-cell-height', heightValue);

    // Also set it on any file-processing components directly
    const fileProcessingElements = document.querySelectorAll('app-file-processing');
    fileProcessingElements.forEach(element => {
      (element as HTMLElement).style.setProperty('--file-cell-height', heightValue);
    });

    // Reset preview selector if current option is not available for the new height
    if (!this.isCurrentPreviewAvailable()) {
      this.selectedPreview = 'none';
    }

    console.log(`File cell height set to: ${heightValue}`);
    console.log('CSS variable verification:', getComputedStyle(document.documentElement).getPropertyValue('--file-cell-height'));

    // Force change detection to ensure UI updates
    this.cdr.detectChanges();
  }

  // Add method to update preview selector
  onPreviewChange(): void {
    console.log('Preview selector changed to:', this.selectedPreview);

    // Implementation of preview change logic will be added here
    // For now, just log the change
    switch (this.selectedPreview) {
      case 'none':
        console.log('Preview disabled');
        break;
      case 'thumbnail':
        console.log('Thumbnail preview enabled');
        break;
      case 'preview':
        console.log('Full preview enabled');
        break;
    }
  }

  // Check if thumbnail option should be enabled
  isThumbnailEnabled(): boolean {
    // Thumbnail is only available for medium and large heights
    return this.selectedHeight === 'medium' || this.selectedHeight === 'large';
  }

  // Check if preview option should be enabled
  isPreviewEnabled(): boolean {
    // Preview is only available for large height
    return this.selectedHeight === 'large';
  }

  // Check if current preview selection is available for current height
  private isCurrentPreviewAvailable(): boolean {
    switch (this.selectedPreview) {
      case 'none':
        return true; // Always available
      case 'thumbnail':
        return this.isThumbnailEnabled();
      case 'preview':
      case 'preview-all':
        return this.isPreviewEnabled();
      default:
        return false;
    }
  }
}
